import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { AttachableConfig, AttachedFile, AttachedEntity } from './attachments.types.js'

const ATTACHMENTS_TABLE = 'file_attachments'
const FILES_TABLE = 'storage_files'

export type AttachmentsService = ReturnType<typeof createAttachmentsService>

export function createAttachmentsService(ctx: ModuleContext) {
  const db = ctx.db.knex

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  function validateEntityType(entityType: string): void {
    if (!entityType || !entityType.includes('.')) {
      throw new (ctx.core.errors as any).ValidationError('entityType must be "module.entity" format')
    }
  }

  function validateMimeType(mimetype: string, accept?: string): void {
    if (!accept) return // no restriction
    const patterns = accept.split(',').map(p => p.trim()).filter(Boolean)
    if (patterns.length === 0) return

    const matched = patterns.some(pattern => {
      if (pattern === '*/*') return true
      if (pattern.endsWith('/*')) {
        const prefix = pattern.slice(0, pattern.indexOf('/'))
        return mimetype.startsWith(prefix + '/')
      }
      return mimetype === pattern
    })

    if (!matched) {
      throw new (ctx.core.errors as any).ValidationError(`File type ${mimetype} not accepted. Allowed: ${accept}`)
    }
  }

  async function validateConstraints(
    entityType: string,
    entityId: string,
    config?: AttachableConfig,
    mimetype?: string
  ): Promise<void> {
    if (!config) return

    if (mimetype) {
      validateMimeType(mimetype, config.accept)
    }

    if (config.maxFiles != null) {
      const countResult = await db(ATTACHMENTS_TABLE)
        .where({ entity_type: entityType, entity_id: entityId })
        .count('* as count')
        .first() as { count: number | string } | undefined

      const current = Number(countResult?.count ?? 0)
      if (current >= config.maxFiles) {
        throw new (ctx.core.errors as any).ValidationError(
          `Maximum ${config.maxFiles} files allowed. Currently ${current} attached.`
        )
      }
    }
  }

  async function validateFileIds(fileIds: string[]): Promise<void> {
    if (fileIds.length === 0) return

    const existing = await db(FILES_TABLE)
      .whereIn('id', fileIds)
      .select('id')

    const existingIds = new Set(existing.map((r: { id: string }) => r.id))
    const missing = fileIds.filter(id => !existingIds.has(id))

    if (missing.length > 0) {
      throw new (ctx.core.errors as any).ValidationError(`Files not found: ${missing.join(', ')}`)
    }
  }

  async function checkEntityAccess(
    entityType: string,
    entityId: string,
    auth: { userId?: string; ability?: any }
  ): Promise<void> {
    const responses = await ctx.events.query<boolean>('storage.checkEntityAccess', {
      entityType, entityId, userId: auth.userId, ability: auth.ability
    })
    if (!responses.some(r => r === true)) {
      throw new (ctx.core.errors as any).ForbiddenError('Cannot access target entity')
    }
  }

  async function getNextSortOrder(entityType: string, entityId: string): Promise<number> {
    const result = await db(ATTACHMENTS_TABLE)
      .where({ entity_type: entityType, entity_id: entityId })
      .max('sort_order as max')
      .first() as { max: number | null } | undefined

    return (result?.max ?? 0) + 1
  }

  // ---------------------------------------------------------------------------
  // Internal CRUD
  // ---------------------------------------------------------------------------

  async function attach(
    fileId: string,
    entityType: string,
    entityId: string,
    sortOrder?: number
  ): Promise<void> {
    const existing = await db(ATTACHMENTS_TABLE)
      .where({ file_id: fileId, entity_type: entityType, entity_id: entityId })
      .first()
    if (existing) return // idempotent

    const order = sortOrder ?? await getNextSortOrder(entityType, entityId)
    const id = ctx.core.generateId()
    await db(ATTACHMENTS_TABLE).insert({
      id,
      file_id: fileId,
      entity_type: entityType,
      entity_id: entityId,
      sort_order: order,
      created_at: ctx.db.nowTimestamp(db)
    })
  }

  async function detach(fileId: string, entityType: string, entityId: string): Promise<void> {
    await db(ATTACHMENTS_TABLE)
      .where({ file_id: fileId, entity_type: entityType, entity_id: entityId })
      .delete()
  }

  async function syncFiles(entityType: string, entityId: string, fileIds: string[]): Promise<void> {
    await validateFileIds(fileIds)

    const existing = await db(ATTACHMENTS_TABLE)
      .where({ entity_type: entityType, entity_id: entityId })
      .select('file_id')

    const existingIds = new Set(existing.map((r: { file_id: string }) => r.file_id))
    const desiredIds = new Set(fileIds)

    const toRemove = [...existingIds].filter(id => !desiredIds.has(id))
    if (toRemove.length > 0) {
      await db(ATTACHMENTS_TABLE)
        .where({ entity_type: entityType, entity_id: entityId })
        .whereIn('file_id', toRemove)
        .delete()
    }

    const toAdd = fileIds.filter(id => !existingIds.has(id))
    if (toAdd.length > 0) {
      let nextOrder = await getNextSortOrder(entityType, entityId)
      const rows = toAdd.map(fileId => ({
        id: ctx.core.generateId(),
        file_id: fileId,
        entity_type: entityType,
        entity_id: entityId,
        sort_order: nextOrder++,
        created_at: ctx.db.nowTimestamp(db)
      }))
      await db(ATTACHMENTS_TABLE).insert(rows)
    }
  }

  async function removeAllForEntity(entityType: string, entityId: string): Promise<void> {
    await db(ATTACHMENTS_TABLE)
      .where({ entity_type: entityType, entity_id: entityId })
      .delete()
  }

  async function removeAllForFile(fileId: string): Promise<void> {
    await db(ATTACHMENTS_TABLE)
      .where({ file_id: fileId })
      .delete()
  }

  // ---------------------------------------------------------------------------
  // Query methods
  // ---------------------------------------------------------------------------

  async function getFilesForEntity(entityType: string, entityId: string): Promise<AttachedFile[]> {
    return db(ATTACHMENTS_TABLE)
      .where({ [`${ATTACHMENTS_TABLE}.entity_type`]: entityType, [`${ATTACHMENTS_TABLE}.entity_id`]: entityId })
      .join(FILES_TABLE, `${FILES_TABLE}.id`, `${ATTACHMENTS_TABLE}.file_id`)
      .select(
        `${FILES_TABLE}.id`,
        `${FILES_TABLE}.filename`,
        `${FILES_TABLE}.mimetype`,
        `${FILES_TABLE}.size`,
        `${FILES_TABLE}.url`,
        `${ATTACHMENTS_TABLE}.sort_order`
      )
      .orderBy(`${ATTACHMENTS_TABLE}.sort_order`, 'asc')
  }

  async function getEntitiesForFile(fileId: string, entityType?: string): Promise<AttachedEntity[]> {
    const query = db(ATTACHMENTS_TABLE).where({ file_id: fileId })
    if (entityType) {
      query.andWhere('entity_type', entityType)
    }
    return query.select('entity_type', 'entity_id')
  }

  async function getFilesForEntities(entityType: string, entityIds: string[]): Promise<Map<string, AttachedFile[]>> {
    const result = new Map<string, AttachedFile[]>()
    if (entityIds.length === 0) return result

    const rows = await db(ATTACHMENTS_TABLE)
      .whereIn(`${ATTACHMENTS_TABLE}.entity_id`, entityIds)
      .andWhere(`${ATTACHMENTS_TABLE}.entity_type`, entityType)
      .join(FILES_TABLE, `${FILES_TABLE}.id`, `${ATTACHMENTS_TABLE}.file_id`)
      .select(
        `${ATTACHMENTS_TABLE}.entity_id`,
        `${FILES_TABLE}.id`,
        `${FILES_TABLE}.filename`,
        `${FILES_TABLE}.mimetype`,
        `${FILES_TABLE}.size`,
        `${FILES_TABLE}.url`,
        `${ATTACHMENTS_TABLE}.sort_order`
      )

    for (const row of rows as (AttachedFile & { entity_id: string })[]) {
      const existing = result.get(row.entity_id) ?? []
      existing.push({
        id: row.id,
        filename: row.filename,
        mimetype: row.mimetype,
        size: row.size,
        url: row.url,
        sort_order: row.sort_order
      })
      result.set(row.entity_id, existing)
    }
    return result
  }

  // ---------------------------------------------------------------------------
  // Public methods (with auth + constraints)
  // ---------------------------------------------------------------------------

  async function syncEntityFiles(
    entityType: string,
    entityId: string,
    fileIds: string[],
    auth: { userId?: string; ability?: any },
    config?: AttachableConfig
  ): Promise<AttachedFile[]> {
    validateEntityType(entityType)
    await checkEntityAccess(entityType, entityId, auth)

    if (config?.maxFiles != null && fileIds.length > config.maxFiles) {
      throw new (ctx.core.errors as any).ValidationError(
        `Maximum ${config.maxFiles} files allowed. Requested ${fileIds.length}.`
      )
    }

    await syncFiles(entityType, entityId, fileIds)
    return getFilesForEntity(entityType, entityId)
  }

  async function attachEntityFile(
    entityType: string,
    entityId: string,
    fileId: string,
    auth: { userId?: string; ability?: any },
    config?: AttachableConfig,
    mimetype?: string
  ): Promise<void> {
    validateEntityType(entityType)
    await checkEntityAccess(entityType, entityId, auth)
    await validateConstraints(entityType, entityId, config, mimetype)
    await attach(fileId, entityType, entityId)
  }

  async function detachEntityFile(
    entityType: string,
    entityId: string,
    fileId: string,
    auth: { userId?: string; ability?: any }
  ): Promise<void> {
    validateEntityType(entityType)
    await checkEntityAccess(entityType, entityId, auth)
    await detach(fileId, entityType, entityId)
  }

  return {
    // Internal
    validateEntityType,
    validateMimeType,
    validateConstraints,
    validateFileIds,
    checkEntityAccess,
    getNextSortOrder,
    attach,
    detach,
    syncFiles,
    removeAllForEntity,
    removeAllForFile,
    getFilesForEntity,
    getEntitiesForFile,
    getFilesForEntities,
    // Public (with auth)
    syncEntityFiles,
    attachEntityFile,
    detachEntityFile,
  }
}
